import React, { useState, useRef, useEffect, useCallback } from 'react';
import { AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { api } from '../../../shared/api/api.js';
import type { Book } from '../../../shared/types/index.js';
import { useBodyScrollLock } from '../../../shared/utils/scrollLock.js';
import {
  ScannerHeader,
  ScannerCameraView,
  ScannerUploadView,
  ScannerManualInput,
  cleanUibnCode,
  detectBarcodeFromVideo,
  decodeBarcodeFromImage,
} from './scanner-modal/index.js';

interface ScannerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onBookFound: (book: Book, scanMethod: 'barcode_scanner' | 'qr_scanner' | 'manual_input') => void;
}

export const ScannerModal: React.FC<ScannerModalProps> = ({ isOpen, onClose, onBookFound }) => {
  const [activeTab, setActiveTab] = useState<'camera' | 'upload'>('camera');
  const [cameraActive, setCameraActive] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [manualCode, setManualCode] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [scanSuccess, setScanSuccess] = useState(false);
  const [facingMode, setFacingMode] = useState<'environment' | 'user'>('environment');
  const [hasMultipleCameras, setHasMultipleCameras] = useState(false);
  const [hasTorch, setHasTorch] = useState(false);
  const [torchActive, setTorchActive] = useState(false);
  const [zoomLevel, setZoomLevel] = useState<number>(1);
  const [zoomCapabilities, setZoomCapabilities] = useState<{ min: number; max: number; step: number } | null>(null);

  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const activeAbortControllerRef = useRef<AbortController | null>(null);
  const activeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Audio beep feedback for instant successful scan
  const playSuccessBeep = () => {
    try {
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, ctx.currentTime); // Crisp A5 note
      gain.gain.setValueAtTime(0.12, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.12);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.12);
    } catch {
      // Audio autoplay restrictions or not supported
    }
  };

  // Vibration feedback for mobile smartphones
  const triggerHaptic = () => {
    try {
      if (typeof navigator !== 'undefined' && navigator.vibrate) {
        navigator.vibrate([40, 25, 40]);
      }
    } catch {}
  };

  // Check available video cameras
  useEffect(() => {
    if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
      navigator.mediaDevices.enumerateDevices().then((devices) => {
        const videoInputs = devices.filter((d) => d.kind === 'videoinput');
        setHasMultipleCameras(videoInputs.length > 1);
      }).catch(() => {});
    }
  }, []);

  // Cancel any active analysis process
  const cancelAnalysis = useCallback((reason?: string) => {
    if (activeTimeoutRef.current) {
      clearTimeout(activeTimeoutRef.current);
      activeTimeoutRef.current = null;
    }
    if (activeAbortControllerRef.current) {
      activeAbortControllerRef.current.abort();
      activeAbortControllerRef.current = null;
    }
    setIsScanning(false);
    setScanSuccess(false);
    if (reason) {
      setErrorMsg(reason);
    }
  }, []);

  // Stop camera stream helper
  const stopCamera = () => {
    cancelAnalysis();
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setCameraActive(false);
    setTorchActive(false);
    setScanSuccess(false);
    setZoomLevel(1);
    setZoomCapabilities(null);
  };

  // Toggle mobile flashlight / torch
  const toggleTorch = async () => {
    if (!streamRef.current) return;
    const track = streamRef.current.getVideoTracks()[0];
    if (track && 'applyConstraints' in track) {
      try {
        const nextState = !torchActive;
        await (track as any).applyConstraints({
          advanced: [{ torch: nextState }],
        });
        setTorchActive(nextState);
      } catch (err) {
        console.warn('Torch toggle failed:', err);
      }
    }
  };

  // Change camera zoom level (hardware optical/digital if supported, fallback to software crop)
  const handleZoomChange = async (newZoom: number) => {
    setZoomLevel(newZoom);
    if (!streamRef.current) return;
    const track = streamRef.current.getVideoTracks()[0];
    if (track && 'applyConstraints' in track && zoomCapabilities) {
      try {
        const targetZoom = Math.min(Math.max(newZoom, zoomCapabilities.min), zoomCapabilities.max);
        await (track as any).applyConstraints({
          advanced: [{ zoom: targetZoom }],
        });
      } catch (err) {
        console.warn('Hardware zoom apply failed:', err);
      }
    }
  };

  // Switch between front and back camera
  const switchCamera = () => {
    stopCamera();
    setFacingMode((prev) => (prev === 'environment' ? 'user' : 'environment'));
  };

  // Re-trigger hardware camera autofocus motor
  const handleRefocus = async () => {
    if (!streamRef.current) return;
    const track = streamRef.current.getVideoTracks()[0];
    if (track && 'applyConstraints' in track) {
      try {
        // Trigger lens motor auto-focus recalibration across macro/continuous modes
        await (track as any).applyConstraints({
          advanced: [
            { focusMode: 'macro' },
            { focusMode: 'continuous' },
            { pointsOfInterest: [{ x: 0.5, y: 0.5 }] },
          ],
        }).catch(() => {
          return (track as any).applyConstraints({
            advanced: [{ focusMode: 'continuous' }],
          });
        }).catch(() => {
          return (track as any).applyConstraints({
            advanced: [{ focusMode: 'auto' }],
          });
        });
      } catch {
        // Ignore constraint rejection
      }
    }
  };

  // Periodic continuous auto-focus pulse interval (re-triggers lens motor every 2.2s for dynamic distance changes)
  useEffect(() => {
    let intervalId: any = null;
    if (cameraActive && streamRef.current) {
      intervalId = setInterval(() => {
        handleRefocus();
      }, 2200);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [cameraActive]);

  // Start camera with crisp high-resolution wide-angle constraints and hardware autofocus
  const startCamera = async () => {
    setErrorMsg(null);
    setScanSuccess(false);
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setErrorMsg('Peramban tidak mendukung akses kamera langsung. Gunakan mode unggah gambar.');
        return;
      }

      let stream: MediaStream;
      try {
        // Tier 1: Request Full HD (1920x1080) with 1280x720 baseline
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: facingMode },
            width: { ideal: 1920, min: 1280 },
            height: { ideal: 1080, min: 720 },
            frameRate: { ideal: 30, min: 15 },
          },
        });
      } catch {
        try {
          // Tier 2: Request HD (1280x720 ideal)
          stream = await navigator.mediaDevices.getUserMedia({
            video: {
              facingMode: { ideal: facingMode },
              width: { ideal: 1280 },
              height: { ideal: 720 },
              frameRate: { ideal: 30 },
            },
          });
        } catch {
          // Tier 3: Standard camera stream
          stream = await navigator.mediaDevices.getUserMedia({
            video: {
              facingMode: { ideal: facingMode },
            },
          });
        }
      }

      streamRef.current = stream;

      // Check for torch, hardware zoom & apply continuous macro focus mode
      const track = stream.getVideoTracks()[0];
      if (track) {
        if ('getCapabilities' in track) {
          const caps = (track as any).getCapabilities ? (track as any).getCapabilities() : {};
          setHasTorch(Boolean(caps.torch));
          if (caps.zoom) {
            setZoomCapabilities({
              min: caps.zoom.min || 1,
              max: Math.min(caps.zoom.max || 5, 5),
              step: caps.zoom.step || 0.1,
            });
          } else {
            setZoomCapabilities(null);
          }
        }

        // Apply continuous autofocus & center point of interest constraint safely after stream start
        if ('applyConstraints' in track) {
          try {
            await (track as any).applyConstraints({
              advanced: [
                { focusMode: 'continuous' },
                { focusMode: 'macro' },
                { pointsOfInterest: [{ x: 0.5, y: 0.5 }] },
              ],
            }).catch(() => {
              (track as any).applyConstraints({ advanced: [{ focusMode: 'continuous' }] }).catch(() => {});
            });
          } catch {
            // Ignore constraint errors
          }
        }
      }

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.setAttribute('playsinline', 'true');
        videoRef.current.play().catch((e) => console.warn('Video play error:', e?.message || e));
      }
      setCameraActive(true);
    } catch (err: any) {
      console.warn('Camera access denied or unavailable:', err);
      setErrorMsg(
        'Akses kamera tidak diizinkan atau tidak tersedia pada peramban/iframe ini. Anda dapat mengunggah foto barcode atau masukkan kode manual.'
      );
      setCameraActive(false);
    }
  };

  useBodyScrollLock(isOpen);

  const handleScanCode = async (code: string, method: 'barcode_scanner' | 'qr_scanner' | 'manual_input') => {
    // Clear previous timeout & controller
    if (activeTimeoutRef.current) clearTimeout(activeTimeoutRef.current);
    const controller = new AbortController();
    activeAbortControllerRef.current = controller;

    setIsScanning(true);
    setErrorMsg(null);

    // Hard limit timeout: 3500ms max for database lookup
    activeTimeoutRef.current = setTimeout(() => {
      cancelAnalysis('Waktu analisis barcode melebihi batas waktu (dibatalkan). Silakan coba lagi dengan pencahayaan yang lebih terang.');
    }, 3500);

    try {
      const cleanCode = cleanUibnCode(code);

      // 1. Direct getBookById resolution (handles UIBN, ID, and title slug)
      try {
        const directRes = await api.getBookById(cleanCode);
        if (controller.signal.aborted) return;
        if (directRes && directRes.book) {
          if (activeTimeoutRef.current) clearTimeout(activeTimeoutRef.current);
          const book = directRes.book;
          // Asynchronously record scan in background without blocking modal close
          api.recordScan(book.uibn, method, book.id).catch(() => {});
          stopCamera();
          onClose();
          onBookFound(book, method);
          return;
        }
      } catch {
        // Fallback to search query
      }

      if (controller.signal.aborted) return;

      // 2. Search query fallback
      const searchRes = await api.searchBooks(cleanCode);
      if (controller.signal.aborted) return;

      if (activeTimeoutRef.current) clearTimeout(activeTimeoutRef.current);

      if (searchRes.books.length > 0) {
        const book = searchRes.books[0];
        // Asynchronously record scan in background without blocking modal close
        api.recordScan(book.uibn, method, book.id).catch(() => {});
        stopCamera();
        onClose();
        onBookFound(book, method);
      } else {
        setScanSuccess(false);
        // Only show explicit error message for manual input to avoid "error fatigue" during auto-scanning
        if (method === 'manual_input') {
          setErrorMsg(`Buku dengan kode atau judul "${cleanCode}" tidak ditemukan dalam database UIBN.`);
        }
      }
    } catch (err: any) {
      if (!controller.signal.aborted) {
        setScanSuccess(false);
        setErrorMsg(err.message || 'Terjadi kesalahan saat memverifikasi kode');
      }
    } finally {
      if (activeTimeoutRef.current) clearTimeout(activeTimeoutRef.current);
      setIsScanning(false);
    }
  };

  // Live Barcode/QR throttled frame detection loop with multi-engine accuracy
  useEffect(() => {
    let animFrameId: number;
    let isSubscribed = true;
    let lastScanTime = 0;
    let isDetecting = false;
    const SCAN_INTERVAL_MS = 130; // ~7.5 scans per second: smooth 60 FPS video, zero CPU lag

    const scanFrame = async (timestamp: number) => {
      if (
        isSubscribed &&
        cameraActive &&
        !isScanning &&
        videoRef.current &&
        videoRef.current.readyState === 4
      ) {
        if (!isDetecting && timestamp - lastScanTime >= SCAN_INTERVAL_MS) {
          lastScanTime = timestamp;
          isDetecting = true;
          try {
            const rawCode = await detectBarcodeFromVideo(videoRef.current, { maxDurationMs: 80, zoomLevel });
            if (rawCode && isSubscribed) {
              setScanSuccess(true);
              playSuccessBeep();
              triggerHaptic();

              // Give user a brief 250ms visual confirmation of green reticle before opening
              setTimeout(() => {
                if (isSubscribed) {
                  const method = rawCode.includes('UIBN') || rawCode.length > 20 ? 'barcode_scanner' : 'qr_scanner';
                  handleScanCode(rawCode, method);
                }
              }, 250);
              return;
            }
          } catch {
            // ignore detection error on silent frames
          } finally {
            isDetecting = false;
          }
        }
      }

      if (cameraActive && isSubscribed) {
        animFrameId = requestAnimationFrame(scanFrame);
      }
    };

    if (cameraActive && activeTab === 'camera') {
      animFrameId = requestAnimationFrame(scanFrame);
    }

    return () => {
      isSubscribed = false;
      if (animFrameId) cancelAnimationFrame(animFrameId);
    };
  }, [cameraActive, activeTab, isScanning, zoomLevel]);

  useEffect(() => {
    if (isOpen && activeTab === 'camera') {
      startCamera();
    } else {
      stopCamera();
    }
    return () => {
      stopCamera();
    };
  }, [isOpen, activeTab, facingMode]);

  // Optical capture button from camera viewfinder with strict timeout
  const handleCaptureFromCamera = async () => {
    if (!videoRef.current) return;
    cancelAnalysis();

    const controller = new AbortController();
    activeAbortControllerRef.current = controller;
    setIsScanning(true);
    setErrorMsg(null);

    // Timeout limit 3.5 seconds
    activeTimeoutRef.current = setTimeout(() => {
      cancelAnalysis('Waktu analisis barcode melebihi batas waktu (dibatalkan). Pastikan garis barcode jelas dan tidak berbayang/silau.');
    }, 3500);

    try {
      const detected = await detectBarcodeFromVideo(videoRef.current, { maxDurationMs: 300 });
      if (controller.signal.aborted) return;

      if (activeTimeoutRef.current) clearTimeout(activeTimeoutRef.current);

      if (detected) {
        setScanSuccess(true);
        playSuccessBeep();
        triggerHaptic();
        setTimeout(() => {
          if (!controller.signal.aborted) {
            handleScanCode(detected, 'barcode_scanner');
          }
        }, 250);
        return;
      }
      setErrorMsg('Tidak mendeteksi kode barcode/QR di bidang kamera. Dekatkan kamera atau posisikan barcode lurus horizontal.');
    } catch (err: any) {
      if (!controller.signal.aborted) {
        setErrorMsg(err.message || 'Gagal memproses pindaian kamera');
      }
    } finally {
      if (activeTimeoutRef.current) clearTimeout(activeTimeoutRef.current);
      setIsScanning(false);
    }
  };

  // Image file upload scanner with strict timeout
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    cancelAnalysis();

    const controller = new AbortController();
    activeAbortControllerRef.current = controller;
    setIsScanning(true);
    setErrorMsg(null);

    activeTimeoutRef.current = setTimeout(() => {
      cancelAnalysis('Waktu analisis gambar barcode melebihi batas waktu (dibatalkan). Silakan gunakan foto yang lebih fokus dan tajam.');
    }, 3500);

    try {
      const detectedCode = await decodeBarcodeFromImage(file, {
        timeoutMs: 3000,
        signal: controller.signal,
      });

      if (controller.signal.aborted) return;
      if (activeTimeoutRef.current) clearTimeout(activeTimeoutRef.current);

      if (detectedCode) {
        await handleScanCode(detectedCode, 'qr_scanner');
      } else {
        setErrorMsg('Gagal mendeteksi Barcode atau QR Code pada gambar. Pastikan gambar tajam, beresolusi cukup, dan tidak blur.');
      }
    } catch (err: any) {
      if (!controller.signal.aborted) {
        setErrorMsg(err.message || 'Format file gambar tidak didukung atau terjadi kegagalan pemrosesan.');
      }
    } finally {
      if (activeTimeoutRef.current) clearTimeout(activeTimeoutRef.current);
      setIsScanning(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 backdrop-blur-xs p-3 sm:p-4 overflow-y-auto overscroll-contain"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          stopCamera();
          onClose();
        }
      }}
    >
      <motion.div
        initial={{ scale: 0.95, y: 15, opacity: 0 }}
        animate={{ scale: 1, y: 0, opacity: 1 }}
        exit={{ scale: 0.95, y: 15, opacity: 0 }}
        transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
        className="bg-white rounded-2xl max-w-xl sm:max-w-2xl w-full my-auto overflow-hidden shadow-2xl border border-slate-100 flex flex-col max-h-[92vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Integrated Compact Header & Tab Switcher */}
        <ScannerHeader
          activeTab={activeTab}
          onTabChange={(tab) => {
            cancelAnalysis();
            setActiveTab(tab);
          }}
          showHelp={showHelp}
          onToggleHelp={() => setShowHelp(!showHelp)}
          onClose={() => {
            stopCamera();
            onClose();
          }}
          cameraActive={cameraActive}
        />

        {/* Body Viewport */}
        <div className="p-3 sm:p-4 overflow-y-auto flex-1">
          <AnimatePresence>
            {errorMsg && (
              <motion.div
                initial={{ opacity: 0, y: -6, height: 0 }}
                animate={{ opacity: 1, y: 0, height: 'auto' }}
                exit={{ opacity: 0, y: -6, height: 0 }}
                transition={{ duration: 0.2 }}
                className="mb-3 p-2.5 rounded-xl bg-rose-50 border border-rose-200 text-rose-800 text-xs flex items-start gap-2 overflow-hidden"
              >
                <AlertCircle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
                <span className="flex-1">{errorMsg}</span>
                <button
                  type="button"
                  onClick={() => setErrorMsg(null)}
                  className="text-rose-500 hover:text-rose-800 font-bold ml-1 cursor-pointer"
                >
                  ✕
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Smooth Animated Tab Content Switch */}
          <AnimatePresence mode="wait" initial={false}>
            {activeTab === 'camera' ? (
              <motion.div
                key="tab-view-camera"
                initial={{ opacity: 0, y: 10, scale: 0.985 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -10, scale: 0.985 }}
                transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
              >
                <ScannerCameraView
                  videoRef={videoRef}
                  cameraActive={cameraActive}
                  isScanning={isScanning}
                  showHelp={showHelp}
                  scanSuccess={scanSuccess}
                  onStartCamera={startCamera}
                  onShowHelp={setShowHelp}
                  onCapture={handleCaptureFromCamera}
                  onCancelScan={() => cancelAnalysis('Analisis barcode dibatalkan oleh pengguna.')}
                  hasTorch={hasTorch}
                  torchActive={torchActive}
                  onToggleTorch={toggleTorch}
                  hasMultipleCameras={hasMultipleCameras}
                  onSwitchCamera={switchCamera}
                  zoomLevel={zoomLevel}
                  onZoomChange={handleZoomChange}
                  isHardwareZoom={Boolean(zoomCapabilities)}
                  onRefocus={handleRefocus}
                />
              </motion.div>
            ) : (
              <motion.div
                key="tab-view-upload"
                initial={{ opacity: 0, y: 10, scale: 0.985 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -10, scale: 0.985 }}
                transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
              >
                <ScannerUploadView
                  isScanning={isScanning}
                  onFileUpload={handleFileUpload}
                  onCancelScan={() => cancelAnalysis('Analisis gambar barcode dibatalkan oleh pengguna.')}
                />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Manual Input Fallback */}
          <ScannerManualInput
            manualCode={manualCode}
            isScanning={isScanning}
            onManualCodeChange={setManualCode}
            onSubmitManual={() => handleScanCode(manualCode.trim(), 'manual_input')}
          />
        </div>
      </motion.div>
    </motion.div>
  );
};
